/**
 * Version badge — small persistent display of the running release.
 *
 * Reads the inlined CalVer release version + git SHA from the bundle via
 * `getAppVersion()`. Renders muted, unobtrusive text suitable for a
 * sidebar footer, page footer, or fixed corner placement.
 *
 * - Release builds (`isReleaseBuild === true`): `v2026.5.3 · abc1234`
 * - Local / non-release builds: `dev`
 *
 * Pass `className` to control positioning, alignment, or padding from the
 * caller — the component itself only sets typography.
 *
 * @module components/blocks/version-badge
 */

import { cn } from '@ui/lib/utils';
import { getAppVersion } from '@ui/lib/version';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface VersionBadgeProps {
	className?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function VersionBadge({ className }: VersionBadgeProps) {
	const { version, shortSha, isReleaseBuild } = getAppVersion();

	const label = isReleaseBuild ? `v${version} · ${shortSha}` : 'dev';

	return (
		<span
			className={cn('select-none font-mono text-[10px] leading-none text-muted-foreground/70', className)}
			title={isReleaseBuild ? `${version} (${shortSha})` : 'local development build'}
		>
			{label}
		</span>
	);
}
